import React, { createContext, useState, useEffect, useContext } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import config from "../config.js";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(Cookies.get("token") || localStorage.getItem("token") || null);
  const [userName, setUserName] = useState(Cookies.get("userName") || localStorage.getItem("userName") || "");
  const [permissions, setPermissions] = useState(() => {
    const savedPermissions = Cookies.get("permissions") || localStorage.getItem("permissions");
    return savedPermissions ? JSON.parse(savedPermissions) : {};
  });
  const [loading, setLoading] = useState(true);

  // Generate a unique session ID
  const generateUniqueId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleLogout = () => {
    Cookies.remove("token", { path: '/' });
    Cookies.remove("userName", { path: '/' });
    Cookies.remove("permissions", { path: '/' });
    Cookies.remove("sessionId", { path: '/' });
    Cookies.remove("userId", { path: '/' });
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    localStorage.removeItem("permissions");
    setToken(null);
    setUserName("");
    setPermissions({});
  };

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((requestConfig) => {
      const activeToken = Cookies.get('token') || localStorage.getItem('token');
      const isApiRequest = typeof requestConfig.url === 'string' &&
        requestConfig.url.startsWith(config.apiBaseUrl);

      if (activeToken && isApiRequest && !requestConfig.headers?.Authorization) {
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers.Authorization = `Bearer ${activeToken}`;
      }

      return requestConfig;
    });

    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // 401 means the session is invalid or expired. 403 means the user is
        // authenticated but lacks permission and must not be logged out.
        if (error.response?.status === 401) {
          handleLogout();
        }
        return Promise.reject(error);
      }
    );

    const initializeAuth = async () => {
      const savedToken = Cookies.get("token") || localStorage.getItem("token");
      const savedUserName = Cookies.get("userName") || localStorage.getItem("userName");
      const savedPermissions = Cookies.get("permissions") || localStorage.getItem("permissions");
      let sessionId = Cookies.get("sessionId");

      if (!sessionId) {
        sessionId = generateUniqueId();
        Cookies.set("sessionId", sessionId, { expires: 1, path: '/' });
      }

      if (savedToken && savedUserName && savedPermissions) {
        setToken(savedToken);
        setUserName(savedUserName);
        setPermissions(JSON.parse(savedPermissions));

        try {
          await axios.post(
            `${config.apiBaseUrl}/api/auth/validate-session`,
            { sessionId },
            { headers: { Authorization: `Bearer ${savedToken}` } }
          );
          setLoading(false);
        } catch (error) {
          if (error.response?.status === 401) {
            handleLogout();
          } else {
            console.warn("Session validation failed, but keeping user signed in:", error);
          }
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ 
      token, 
      setToken, 
      userName, 
      setUserName, 
      permissions, 
      setPermissions, 
      loading, 
      handleLogout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
